import React, { useState, useEffect } from 'react';
import { Button } from '@mui/material';
import { Crown, Gem, User } from 'lucide-react';
import axios from "axios";
import './Leaderboard.css';

axios.defaults.baseURL = process.env.REACT_APP_BACKEND_API_BASE_ENDPOINT;

const Leaderboard = ({ n = 100 }) => {
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Check if screen is mobile
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Inline styles to replace the external CSS
  const styles = {
    leaderboardContainer: {
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      maxWidth: '800px',
      margin: '0 auto',
      padding: isMobile ? '12px' : '20px'
    },
    leaderboardCard: {
      background: 'white',
      borderRadius: '12px',
      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
      overflow: 'hidden',
      transition: 'transform 0.2s ease, box-shadow 0.2s ease'
    },
    leaderboardHeader: {
      background: '#E9E9E9',
      padding: isMobile ? '16px' : '24px',
      color: 'white'
    },
    leaderboardTitle: {
      fontSize: isMobile ? '18px' : '24px',
      fontWeight: '700',
      margin: '0',
      display: 'flex',
      alignItems: 'center',
      gap: isMobile ? '8px' : '12px'
    },
    leaderboardTable: {
      width: '100%',
      borderCollapse: 'collapse'
    },
    tableHeader: {
      background: '#f8fafc',
      borderBottom: '2px solid #e2e8f0'
    },
    tableHeaderTh: {
      padding: isMobile ? '12px 8px' : '16px 24px',
      textAlign: 'left',
      fontSize: isMobile ? '10px' : '12px',
      fontWeight: '600',
      color: '#64748b',
      textTransform: 'uppercase',
      letterSpacing: '0.05em'
    },
    tableRow: {
      borderBottom: '1px solid #f1f5f9',
      transition: 'background-color 0.15s ease'
    },
    tableRowRank1: {
      background: 'linear-gradient(90deg, #fef3c7 0%, #fef9e7 100%)',
      borderLeft: '4px solid #f59e0b'
    },
    tableRowRank2: {
      background: 'linear-gradient(90deg, #f1f5f9 0%, #f8fafc 100%)',
      borderLeft: '4px solid #94a3b8'
    },
    tableRowRank3: {
      background: 'linear-gradient(90deg, #fed7aa 0%, #fef3e2 100%)',
      borderLeft: '4px solid #ea580c'
    },
    tableCell: {
      padding: isMobile ? '12px 8px' : '20px 24px',
      verticalAlign: 'middle'
    },
    rankCell: {
      width: isMobile ? '50px' : '80px'
    },
    rankNumber: {
      fontSize: isMobile ? '14px' : '18px',
      fontWeight: '700',
      color: '#64748b'
    },
    crownIcon: {
      color: '#f59e0b',
      filter: 'drop-shadow(0 2px 4px rgba(245, 158, 11, 0.3))'
    },
    nameCell: {
      fontSize: isMobile ? '13px' : '16px',
      fontWeight: '600',
      color: '#1e293b',
      wordBreak: 'break-word',
      lineHeight: '1.2'
    },
    accountTypeCell: {
      textAlign: 'center',
      width: isMobile ? '60px' : '100px'
    },
    accountIcon: {
      display: 'inline-flex',
      padding: isMobile ? '6px' : '8px',
      borderRadius: '8px',
      transition: 'transform 0.15s ease'
    },
    accountIconPaid: {
      background: '#fef3c7',
      color: '#d97706'
    },
    accountIconFree: {
      background: '#f1f5f9',
      color: '#64748b'
    },
    scoreCell: {
      textAlign: 'right',
      width: isMobile ? '80px' : '120px'
    },
    scoreValue: {
      fontSize: isMobile ? '14px' : '18px',
      fontWeight: '700',
      color: '#1e293b',
      background: 'linear-gradient(135deg, #667eea, #764ba2)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text'
    },
    loadingSpinner: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '40px'
    },
    spinner: {
      width: '32px',
      height: '32px',
      border: '3px solid #f1f5f9',
      borderTop: '3px solid #667eea',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    },
    errorMessage: {
      background: '#fef2f2',
      border: '1px solid #fecaca',
      borderRadius: '8px',
      padding: '16px',
      color: '#dc2626',
      fontWeight: '500',
      margin: '20px'
    },
    emptyState: {
      textAlign: 'center',
      padding: '40px',
      color: '#64748b',
      fontSize: '16px'
    }
  };

  useEffect(() => {
    fetchTopUsers();
    console.log(filter);
  }, [n, filter]);

  // Function to fetch real data from API
  const fetchTopUsers = async () => {
    setLoading(true);
    setError(null);
    
    try {
        const response = await axios.get(`/users/get-top-n-users/${filter}/${n}`);
        const data = response.data;

        setUsers(data); // assumes success if no error is thrown
        } catch (err) {
        if (err.response) {
            setError(err.response.data.error || 'Failed to fetch users');
        } else {
            setError('Network error occurred');
        }
        } finally {
        setLoading(false);
    }
  };

  const getAccountIcon = (accountType) => {
    const iconSize = isMobile ? 16 : 20;
    return accountType === 'paid' ? (
      <div style={{...styles.accountIcon, ...styles.accountIconPaid}}>
        <Gem size={iconSize} />
      </div>
    ) : (
      <div style={{...styles.accountIcon, ...styles.accountIconFree}}>
        <User size={iconSize} />
      </div>
    );
  };

  const getRankIcon = (rank) => {
    const iconSize = isMobile ? 20 : 24;
    if (rank === 1) return <Crown size={iconSize} style={styles.crownIcon} />;
    return <span style={styles.rankNumber}>#{rank}</span>;
  };

  if (loading) {
    return (
      <div style={styles.loadingSpinner}>
        <div style={styles.spinner}></div>
      </div>
    );
  }

  const changeType = (filter_choice) => {
    setFilter(filter_choice);   
  }

  if (error) {
    return (
      <div style={styles.errorMessage}>
        Error: {error}
      </div>
    );
  }

  return (
    <div style={styles.leaderboardContainer}>
      <div style={styles.leaderboardCard}>
        <div style={styles.leaderboardHeader}>
          <h2 style={styles.leaderboardTitle}>
            <Crown size={isMobile ? 20 : 24} />
            Placar - Top {n} usuários (fase experimental)
          </h2>
          <div className='flex-row-lb'>
              <Button type='submit' variant='contained' className={`${filter === 'all' ? 'std-button-on' : 'std-button-off'}`} onClick={() => changeType('all')}>todos</Button>
              <Button type='submit' variant='contained' className={`${filter === 'paid' ? 'std-button-on' : 'std-button-off'}`} onClick={() => changeType('paid')}>premium</Button>
              <Button type='submit' variant='contained' className={`${filter === 'free' ? 'std-button-on' : 'std-button-off'}`} onClick={() => changeType('free')}>gratuito</Button>
          </div>
        </div>

        <table style={styles.leaderboardTable}>
          <thead style={styles.tableHeader}>
            <tr>
              <th style={styles.tableHeaderTh}>#</th>
              <th style={styles.tableHeaderTh}>Nome</th>
              <th style={{...styles.tableHeaderTh, textAlign: 'center'}}>Tipo de conta</th>
              <th style={{...styles.tableHeaderTh, textAlign: 'right'}}>Score</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user, index) => {
              const rank = index + 1;
              let rowStyle = styles.tableRow;
              if (rank === 1) rowStyle = {...styles.tableRow, ...styles.tableRowRank1};
              else if (rank === 2) rowStyle = {...styles.tableRow, ...styles.tableRowRank2};
              else if (rank === 3) rowStyle = {...styles.tableRow, ...styles.tableRowRank3};
              
              return (
                <tr key={user.user_id} style={rowStyle}>
                  <td style={{...styles.tableCell, ...styles.rankCell}}>
                    {getRankIcon(rank)}
                  </td>
                  <td style={{...styles.tableCell, ...styles.nameCell}}>
                    {user.full_name}
                  </td>
                  <td style={{...styles.tableCell, ...styles.accountTypeCell}}>
                    {getAccountIcon(user.account_type)}
                  </td>
                  <td style={{...styles.tableCell, ...styles.scoreCell}}>
                    <div style={styles.scoreValue}>{user.score}</div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {users.length === 0 && !loading && (
          <div style={styles.emptyState}>
            No users found
          </div>
        )}
      </div>
    </div>
  );
};

export {Leaderboard};